import {
  Button,
  buttonVariants,
  Chip,
  cn,
  EmptyState,
  ErrorBanner,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Picker,
  plainMarkup,
  QueueRow,
  SkeletonRows,
  useIsSinglePane,
  useKey,
} from '@patchbay/ui'
import { useInfiniteQuery, useQueries } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, ChevronsUpDown, Eye, Inbox, MoreHorizontal } from 'lucide-react'
import * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import type { CaseRow } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { KEYS } from '../../lib/keys'
import { unreadOf } from '../../lib/unread'
import { validBrandScope } from '../brands/api'
import { useBrandScope } from '../brands/use-brand-scope'
import { BlockerChips, CLOSEOUT_BATCH, closeoutBatchQuery } from '../closeout'
import { renderCaseParty } from '../directory/party-link'
import { type CaseListParams, type ClosedRange, DEFAULT_CLOSED_RANGE, queueErrorText } from './api'
import { type ApiLane, QUEUE_LANES, queueLanePresentation } from './lanes'
import { caseListQuery } from './queries'
import { QueueKeyboardFooter } from './queue-keyboard-footer'
import { QueueRail, useDebouncedQueueRailRow } from './queue-rail'
import { ExpandedCase, NO_SUMMARY, QueuePeekAction, QueuePeekGutter, rowWho } from './queue-row'
import { VirtualQueueList } from './virtual-list'

export type CaseListScope = CaseListParams['scope']
export type CaseListSearch = Omit<CaseListParams, 'scope'> & { brand?: string }

const STATUS_OPTIONS: { value: NonNullable<CaseListParams['status']>; label: string }[] = [
  { value: 'reply_owed', label: 'Reply owed' },
  { value: 'action_owed', label: 'Action owed' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'waiting_contractor', label: 'Waiting on contractor' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'closed', label: 'Closed' },
]

const SORT_OPTIONS: { value: NonNullable<CaseListParams['sort']>; label: string }[] = [
  { value: 'activity', label: 'Last activity' },
  { value: 'number', label: 'Case number' },
  { value: 'customer', label: 'Customer' },
  { value: 'lane', label: 'Lane' },
  { value: 'due', label: 'Next move due' },
  { value: 'closed', label: 'Closed date' },
]

const CLOSED_RANGES: { value: ClosedRange; label: string }[] = [
  { value: '7', label: 'Closed in the last 7 days' },
  { value: '30', label: 'Closed in the last 30 days' },
  { value: '90', label: 'Closed in the last 90 days' },
  { value: 'all', label: 'Closed any time' },
  { value: 'custom', label: 'Closed between…' },
]
const RANGES = new Set<string>(CLOSED_RANGES.map((r) => r.value))
const DAY = /^\d{4}-\d{2}-\d{2}$/

const TITLES: Record<CaseListScope, string> = { open: 'All open', all: 'All cases', closed: 'Closed' }
const NAV_IDS: Record<CaseListScope, string> = { open: 'all-open', all: 'all-cases', closed: 'closed' }

function closedOn(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : formatWhen(d.toISOString(), { style: 'date' })
}

const LANE_FILTERS = QUEUE_LANES.filter((lane) => !['ledger', 'proposals', 'overdue_by_tech'].includes(lane.api))

const STATUSES = new Set<string>(STATUS_OPTIONS.map((o) => o.value))
const SORTS = new Set<string>(SORT_OPTIONS.map((o) => o.value))
const LANES = new Set<string>(QUEUE_LANES.map((l) => l.api))

/** URL search params → filters; anything unrecognised is dropped rather than sent to the API. */
export function parseCaseListSearch(raw: Record<string, unknown>): CaseListSearch {
  const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim().slice(0, 100) : undefined)
  const status = text(raw.status)
  const sort = text(raw.sort)
  const lane = text(raw.lane)
  const order = text(raw.order)
  const closed = text(raw.closed)
  const closedFrom = text(raw.closedFrom)
  const closedTo = text(raw.closedTo)
  return {
    brand: validBrandScope(raw.brand),
    closed: closed && RANGES.has(closed) ? (closed as ClosedRange) : undefined,
    closedFrom: closedFrom && DAY.test(closedFrom) ? closedFrom : undefined,
    closedTo: closedTo && DAY.test(closedTo) ? closedTo : undefined,
    customer: text(raw.customer),
    status: status && STATUSES.has(status) ? (status as CaseListSearch['status']) : undefined,
    lane: lane && LANES.has(lane) ? (lane as ApiLane) : undefined,
    sort: sort && SORTS.has(sort) ? (sort as CaseListSearch['sort']) : undefined,
    order: order === 'asc' || order === 'desc' ? order : undefined,
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = React.useState(value)
  React.useEffect(() => {
    const timer = window.setTimeout(() => setCurrent(value), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])
  return current
}

const SELECT_CLASS = 'h-8 rounded-md border border-input bg-card px-2 text-sm text-foreground'

/** All open (every non-closed case, any lane), All cases (closed included), #89 item 7, and Closed (#271: recently
 * closed, newest close first, with a date range; a closed row shows its close date and any close-out blocker). */
export function CaseListScreen({
  scope,
  search,
  onSearch,
}: {
  scope: CaseListScope
  search: CaseListSearch
  onSearch: (next: CaseListSearch) => void
}) {
  const brandScope = useBrandScope()
  const navigate = useNavigate()
  const singlePane = useIsSinglePane()
  const [customerText, setCustomerText] = React.useState(search.customer ?? '')
  const debouncedCustomer = useDebounced(customerText, 300)
  React.useEffect(() => {
    if ((debouncedCustomer.trim() || undefined) !== search.customer)
      onSearch({ ...search, customer: debouncedCustomer.trim() || undefined })
  }, [debouncedCustomer, onSearch, search])

  const closedScope = scope === 'closed'
  const { brand: _brand, ...filters } = search
  const params: CaseListParams = {
    scope,
    ...filters,
    status: closedScope ? undefined : search.status,
    closed: closedScope ? (search.closed ?? DEFAULT_CLOSED_RANGE) : undefined,
    sort: search.sort ?? (closedScope ? 'closed' : 'activity'),
    order: search.order ?? 'desc',
  }
  const query = useInfiniteQuery(caseListQuery(params, brandScope.param))
  const rows = React.useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data])
  const total = query.data?.pages[0]?.total ?? rows.length
  const title = TITLES[scope]
  // Close-out state for the Closed view's rows: a closed case with work left shows it here, never in a work lane.
  const closedIds = React.useMemo(() => (closedScope ? rows.map((r) => r.id) : []), [closedScope, rows])
  const closeoutChunks = React.useMemo(() => {
    const out: string[][] = []
    for (let i = 0; i < closedIds.length; i += CLOSEOUT_BATCH) out.push(closedIds.slice(i, i + CLOSEOUT_BATCH))
    return out
  }, [closedIds])
  const closeoutQueries = useQueries({ queries: closeoutChunks.map((ids) => closeoutBatchQuery(ids)) })
  const closeoutOf = (id: string) => {
    for (const q of closeoutQueries) {
      const hit = q.data?.get(id)
      if (hit) return hit
    }
    return undefined
  }

  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [scrollRequest, setScrollRequest] = React.useState(0)
  const [expandedId, setExpandedId] = React.useState<string | null>(null)
  const found = selectedId ? rows.findIndex((row) => row.id === selectedId) : -1
  const selectedIndex = found >= 0 ? found : 0
  const selected = rows[selectedIndex]
  const railRow = useDebouncedQueueRailRow(selected)

  const move = React.useCallback(
    (by: number) => {
      if (rows.length === 0) return
      const next = Math.max(0, Math.min(rows.length - 1, selectedIndex + by))
      setSelectedId(rows[next]?.id ?? null)
      setScrollRequest((n) => n + 1)
    },
    [rows, selectedIndex],
  )
  const jump = React.useCallback(
    (index: number) => {
      if (rows.length === 0) return
      const next = Math.max(0, Math.min(rows.length - 1, index))
      setSelectedId(rows[next]?.id ?? null)
      setScrollRequest((request) => request + 1)
    },
    [rows],
  )
  const peek = React.useCallback((row: CaseRow | undefined) => {
    if (row) setSelectedId(row.id)
  }, [])
  const toggleExpanded = React.useCallback((row: CaseRow | undefined) => {
    if (row) setExpandedId((current) => (current === row.id ? null : row.id))
  }, [])
  const open = React.useCallback(
    (row: CaseRow | undefined) => {
      if (row) navigate({ to: '/case/$id', params: { id: row.number } })
    },
    [navigate],
  )
  useKey(KEYS.down.keys, () => move(1), {}, [move])
  useKey(KEYS.up.keys, () => move(-1), {}, [move])
  useKey('arrowdown', () => move(1), {}, [move])
  useKey('arrowup', () => move(-1), {}, [move])
  useKey(KEYS.first.keys, () => jump(0), {}, [jump])
  useKey(KEYS.last.keys, () => jump(rows.length - 1), {}, [jump, rows.length])
  useKey(KEYS.peek.keys, () => peek(selected), {}, [peek, selected])
  useKey(KEYS.open.keys, () => open(selected), {}, [open, selected])
  useKey(KEYS.expand.keys, () => toggleExpanded(selected), {}, [selected, toggleExpanded])

  React.useEffect(() => {
    if (expandedId && !rows.some((row) => row.id === expandedId)) setExpandedId(null)
  }, [expandedId, rows])

  const set = (patch: Partial<CaseListSearch>) => onSearch({ ...search, ...patch })
  const order = params.order
  const toolbar = (
    <fieldset className="m-0 flex flex-wrap items-center gap-1.5 border-0 p-0" aria-label={`${title} filters`}>
      <Input
        aria-label="Search cases"
        placeholder="Case, ticket, customer, subject"
        value={customerText}
        onChange={(event) => setCustomerText(event.target.value)}
        className="w-44"
      />
      {closedScope ? (
        <>
          <Picker
            aria-label="Closed when"
            className={SELECT_CLASS}
            value={params.closed ?? ''}
            onChange={(event) => set({ closed: event.target.value as ClosedRange })}
          >
            {CLOSED_RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Picker>
          {params.closed === 'custom' ? (
            <>
              <Input
                aria-label="Closed from"
                type="date"
                className="w-36"
                value={search.closedFrom ?? ''}
                onChange={(event) => set({ closedFrom: event.target.value || undefined })}
              />
              <Input
                aria-label="Closed to"
                type="date"
                className="w-36"
                value={search.closedTo ?? ''}
                onChange={(event) => set({ closedTo: event.target.value || undefined })}
              />
            </>
          ) : null}
        </>
      ) : (
        <Picker
          aria-label="Filter by status"
          className={SELECT_CLASS}
          value={search.status ?? ''}
          onChange={(event) => set({ status: (event.target.value || undefined) as CaseListSearch['status'] })}
        >
          <option value="">Any status</option>
          {STATUS_OPTIONS.filter((o) => scope === 'all' || o.value !== 'closed').map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Picker>
      )}
      <Picker
        aria-label="Filter by lane"
        className={SELECT_CLASS}
        value={search.lane ?? ''}
        onChange={(event) => set({ lane: (event.target.value || undefined) as ApiLane | undefined })}
      >
        <option value="">Any lane</option>
        {LANE_FILTERS.map((l) => (
          <option key={l.api} value={l.api}>
            {l.label}
          </option>
        ))}
      </Picker>
      <Picker
        aria-label="Sort by"
        className={SELECT_CLASS}
        value={params.sort ?? ''}
        onChange={(event) => set({ sort: event.target.value as CaseListSearch['sort'] })}
      >
        {SORT_OPTIONS.filter((o) => closedScope || o.value !== 'closed').map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Picker>
      <Button
        size="icon-sm"
        variant="ghost"
        aria-label={order === 'desc' ? 'Sorted descending; sort ascending' : 'Sorted ascending; sort descending'}
        onClick={() => set({ order: order === 'desc' ? 'asc' : 'desc' })}
      >
        {order === 'desc' ? <ArrowDownWideNarrow /> : <ArrowUpNarrowWide />}
      </Button>
    </fieldset>
  )

  return (
    <Frame
      active={NAV_IDS[scope]}
      main={
        <>
          <PaneHeader
            title={title}
            subtitle={`${total} ${scope === 'open' ? 'open' : scope === 'closed' ? 'closed' : 'cases, open and closed'} · ${rows.length} loaded${closedScope ? '' : ' · any lane'}`}
            actions={toolbar}
          />
          {query.isError && rows.length > 0 ? (
            <div className="border-b border-border px-4 py-2">
              <ErrorBanner title={`${title} couldn't refresh`} detail="Showing stale rows." onRetry={() => void query.refetch()} />
            </div>
          ) : null}
          {query.isPending ? (
            <SkeletonRows count={6} />
          ) : query.isError && rows.length === 0 ? (
            <div className="p-4">
              <ErrorBanner title={`${title} unreachable`} detail={queueErrorText(query.error)} onRetry={() => void query.refetch()} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<Inbox />} title="No case matched these filters." />
          ) : (
            <VirtualQueueList
              items={rows}
              getKey={(row) => row.id}
              selectedIndex={selectedIndex}
              scrollRequest={scrollRequest}
              label={title}
              onNearEnd={query.hasNextPage && !query.isFetchingNextPage ? () => void query.fetchNextPage() : undefined}
              renderItem={(row) => {
                const who = rowWho(row)
                const lane = row.lane ? queueLanePresentation(row.lane) : null
                const closeout = closedScope ? closeoutOf(row.id) : undefined
                return (
                  <div className="group relative [&>[role=option]]:pl-7">
                    <QueuePeekGutter label={row.number} selected={row.id === selected?.id} onPeek={() => peek(row)} />
                    <QueueRow
                      id={row.id}
                      customer={who.text}
                      renderCustomer={renderCaseParty(row)}
                      customerHint={who.unresolved && row.contact_display ? 'account not resolved' : undefined}
                      title={`${row.number} · ${row.title}`}
                      tickets={row.tickets.filter((t) => !t.tombstoned).map((t) => t.ticket_number)}
                      owner={row.owner ? { name: row.owner.name } : undefined}
                      unread={unreadOf(row)}
                      draftReady={row.draft_ready}
                      summary={plainMarkup(row.summary_line) || NO_SUMMARY}
                      nextMove={row.next_move?.text || 'set a next move'}
                      chips={
                        <>
                          {row.conversation_state === 'closed' ? (
                            <Chip>{closedOn(row.closed_at) ? `Closed ${closedOn(row.closed_at)}` : 'Closed'}</Chip>
                          ) : null}
                          {lane ? <Chip tone={lane.tone}>{lane.label}</Chip> : null}
                          {closeout ? <BlockerChips data={closeout} max={2} /> : null}
                        </>
                      }
                      selected={row.id === selected?.id}
                      expanded={expandedId === row.id}
                      expandedContent={expandedId === row.id ? <ExpandedCase row={row} /> : undefined}
                      actions={
                        singlePane ? undefined : (
                          <div
                            role="toolbar"
                            aria-label="Row actions"
                            className={`flex items-center gap-0.5 rounded-md border border-border bg-card/95 p-0.5 shadow-sm transition-opacity ${row.id === selected?.id ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100'}`}
                          >
                            <QueuePeekAction selected={row.id === selected?.id} onPeek={() => peek(row)} />
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label="Expand row"
                              title="Last turn (Space)"
                              onClick={(event) => {
                                event.stopPropagation()
                                toggleExpanded(row)
                              }}
                            >
                              <ChevronsUpDown />
                            </Button>
                          </div>
                        )
                      }
                      footer={
                        singlePane ? (
                          <Menu>
                            <MenuTrigger
                              aria-label="Row actions"
                              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'min-h-11')}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <MoreHorizontal className="size-4" /> Actions
                            </MenuTrigger>
                            <MenuContent>
                              <MenuItem onClick={() => peek(row)}>
                                <Eye /> Peek
                              </MenuItem>
                              <MenuItem onClick={() => open(row)}>Open</MenuItem>
                              <MenuItem onClick={() => toggleExpanded(row)}>
                                <ChevronsUpDown /> Last turn
                              </MenuItem>
                            </MenuContent>
                          </Menu>
                        ) : null
                      }
                      onSelect={() => {
                        peek(row)
                        open(row)
                      }}
                      onOpen={() => open(row)}
                    />
                  </div>
                )
              }}
            />
          )}
          <QueueKeyboardFooter
            hasRows={rows.length > 0}
            atFirst={selectedIndex <= 0}
            atLast={selectedIndex >= rows.length - 1}
            onPrevious={() => move(-1)}
            onNext={() => move(1)}
            onFirst={() => jump(0)}
            onLast={() => jump(rows.length - 1)}
            onPeek={() => peek(selected)}
            onOpen={() => open(selected)}
            onExpand={() => toggleExpanded(selected)}
            trailing={
              query.isFetchingNextPage ? (
                <span role="status">Loading more…</span>
              ) : query.hasNextPage ? (
                <Button size="sm" variant="ghost" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                  Load more ({rows.length} of {total})
                </Button>
              ) : rows.length > 0 ? (
                <span>End of list · {total}</span>
              ) : null
            }
          />
        </>
      }
      rail={<QueueRail key={railRow?.id ?? 'none'} row={railRow} />}
    />
  )
}
